// Detalle de la lección con layout 2 columnas: vocab izquierda fijo + speaking/activities derecha.
// Filtrado por licencia ya viene aplicado del backend (visible_speaking_slots).

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { SpeakingBlockView } from '@/components/molecules/SpeakingBlockView';
import { VocabPanel } from '@/components/molecules/VocabPanel';
import { Header } from '@/components/organisms/Header';
import { LessonContentDrawer } from '@/components/organisms/LessonContentDrawer';
import { NotepadFloatingModal } from '@/components/organisms/NotepadFloatingModal';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import { getLessonContent, type LessonContent } from '@/services/lessons';
import { safeHtml } from '@/utils/safeHtml';
import styles from './Lecciones.module.scss';

export function LessonDetail() {
  const { id: idStr } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const bookingId = Number(searchParams.get('booking'));
  const inActiveClass = Number.isInteger(bookingId) && bookingId > 0;
  const { state } = useAuth();
  const isAdmin = state.status === 'authenticated' && state.user.roles.includes('admin');
  const isStudent = state.status === 'authenticated' && state.user.roles.includes('student');

  const [content, setContent] = useState<LessonContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [notepadOpen, setNotepadOpen] = useState(false);

  const lessonId = idStr ? Number(idStr) : NaN;

  const reload = useCallback(async () => {
    if (!Number.isInteger(lessonId) || lessonId < 1) {
      setError('Id inválido');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const c = await getLessonContent(lessonId);
      setContent(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => { void reload(); }, [reload]);

  const lesson = content?.lesson;
  const visibleSpeaking = content?.speaking_blocks ?? [];

  return (
    <>
      <Header
        title={lesson?.title ?? 'Lección'}
        actions={
          <>
            {isStudent && inActiveClass && lesson && (
              <Button variant="secondary" onClick={() => setNotepadOpen((v) => !v)}>
                <Icon name="notebook-pen" size={14} /> Mi cuaderno
              </Button>
            )}
            {isAdmin && lesson && (
              <Button variant="secondary" onClick={() => setEditorOpen(true)}>
                <Icon name="book" size={14} /> Editar contenido
              </Button>
            )}
          </>
        }
      />

      <main className={styles.lessonMain}>
        <Link
          to={lesson?.module_id ? `/lecciones/modulo/${lesson.module_id}` : '/lecciones'}
          className={styles.backLink}
        >
          <Icon name="chevron-left" size={16} /> Volver
        </Link>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : !content || !lesson ? null : (
          <>
            <header className={styles.lessonHeader}>
              <div className={styles.moduleMeta}>
                <Badge tone="accent">{lesson.level_label}</Badge>
                {content.license_slug && <Badge tone="neutral">Licencia: {content.license_slug}</Badge>}
                <span className={styles.muted}>
                  Visible: speakings {content.visible_speaking_slots.join(', ') || '—'}
                </span>
              </div>
              <h2 className={styles.moduleTitle}>{lesson.title}</h2>
              {lesson.overview && (
                <div className={styles.lead} dangerouslySetInnerHTML={{ __html: safeHtml(lesson.overview) }} />
              )}
            </header>

            {lesson.warmup && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Warm-up</h3>
                <div className={styles.richText} dangerouslySetInnerHTML={{ __html: safeHtml(lesson.warmup) }} />
              </section>
            )}

            <div className={styles.twoColumns}>
              <VocabPanel items={content.vocab_items} />

              <div className={styles.rightColumn}>
                {visibleSpeaking.length === 0 ? (
                  <div className={styles.placeholderBox}>
                    <p>Esta lección aún no tiene contenido speaking visible para tu licencia.</p>
                    <p className={styles.muted}>Tu licencia permite ver: speakings {content.visible_speaking_slots.join(', ') || 'ninguno'}.</p>
                  </div>
                ) : (
                  <div className={styles.speakingList}>
                    {visibleSpeaking.map((block) => (
                      <SpeakingBlockView key={block.block_index} block={block} />
                    ))}
                  </div>
                )}

                <section className={styles.activitiesSection}>
                  <h3 className={styles.sectionTitle}>Activities</h3>
                  {content.activities.length === 0 ? (
                    <p className={styles.muted}>Sin actividades programadas en esta lección.</p>
                  ) : (
                    <ul className={styles.activitiesList}>
                      {content.activities.map((a) => (
                        <li key={a.position} className={styles.activityRow}>
                          <span className={styles.activityNum}>{String(a.position).padStart(2, '0')}</span>
                          <div className={styles.activityBody}>
                            {a.enunciado && (
                              <div className={styles.activityEnunciado} dangerouslySetInnerHTML={{ __html: safeHtml(a.enunciado) }} />
                            )}
                            <span className={styles.muted}>
                              {a.quiz_title ? `Quiz: ${a.quiz_title}` : 'Sin quiz vinculado'}
                            </span>
                          </div>
                          {a.quiz_id ? (
                            <Button asChild variant="primary" size="sm">
                              <Link to={`/quiz/${a.quiz_id}?lesson=${lesson.id}`}>Empezar</Link>
                            </Button>
                          ) : (
                            <Badge tone="warning">Sin quiz</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            </div>
          </>
        )}
      </main>

      {lesson && (
        <LessonContentDrawer
          open={editorOpen}
          onOpenChange={setEditorOpen}
          lessonId={lesson.id}
          initialVocab={content?.vocab_items ?? []}
          initialSpeaking={content?.speaking_blocks ?? []}
          initialActivities={content?.activities ?? []}
          onSaved={() => { setEditorOpen(false); void reload(); }}
        />
      )}

      {isStudent && inActiveClass && lesson && (
        <NotepadFloatingModal
          lessonId={lesson.id}
          open={notepadOpen}
          onOpenChange={setNotepadOpen}
        />
      )}
    </>
  );
}
